/**
 * Dashboard layout shell — BrandHeader, tab nav, filter bar, scrollable
 * main content area, and AppFooter. Provides the data context (fetch +
 * filter) so individual pages can just consume `useDashboardContext()`.
 */

import { createContext, useContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { BrandHeader } from './BrandHeader';
import { AppFooter } from './AppFooter';
import { DashboardNav } from '@/components/dashboard/DashboardNav';
import { DashboardFilterBar } from '@/components/dashboard/DashboardFilterBar';
import { useDashboardData } from '@/lib/dashboard/useDashboardData';
import { useDashboardFilters, applyFilters } from '@/lib/dashboard/filters';
import type { DashboardObservation, DashboardFilters } from '@/lib/dashboard/types';
import type { DashboardUser } from '@/lib/dashboard/api';
import { UserBadge } from '@/components/auth/UserBadge';

interface DashboardContextValue {
  /** All observations (unfiltered). */
  all: DashboardObservation[];
  /** Observations after active filters applied. */
  filtered: DashboardObservation[];
  loading: boolean;
  error: string | null;
  fetchedAt: string | null;
  user: DashboardUser | null;
  filters: DashboardFilters;
  setFilters: (patch: Partial<DashboardFilters>) => void;
  refetch: () => void;
}

const Ctx = createContext<DashboardContextValue | null>(null);

export function useDashboardContext(): DashboardContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDashboardContext must be used within DashboardShell');
  return v;
}

export function DashboardShell() {
  const { observations, loading, error, fetchedAt, user, refetch } = useDashboardData();
  const { filters, setFilters } = useDashboardFilters();
  const filtered = useMemo(() => applyFilters(observations, filters), [observations, filters]);

  const ctx: DashboardContextValue = useMemo(
    () => ({ all: observations, filtered, loading, error, fetchedAt, user, filters, setFilters, refetch }),
    [observations, filtered, loading, error, fetchedAt, user, filters, setFilters, refetch],
  );

  return (
    <Ctx.Provider value={ctx}>
      <div className="flex h-full flex-col">
        <BrandHeader title="FCCERS-3 Dashboard">
          <UserBadge role={user?.isAdmin ? 'Admin' : 'Observer'} />
        </BrandHeader>
        <DashboardNav />
        <DashboardFilterBar />
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-muted/30">
          {loading ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-dece-navy border-t-transparent" />
                <span className="text-sm">Loading dashboard data...</span>
              </div>
            </div>
          ) : error ? (
            <div className="mx-auto mt-12 max-w-lg rounded-lg border border-destructive/30 bg-destructive/10 px-6 py-4 text-center text-sm text-destructive">
              <p className="font-medium">Could not load observations</p>
              <p className="mt-1 text-xs">{error}</p>
              <button
                onClick={refetch}
                className="mt-3 rounded-md bg-destructive px-4 py-1.5 text-xs font-medium text-destructive-foreground hover:bg-destructive/90"
              >
                Retry
              </button>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
        <AppFooter />
      </div>
    </Ctx.Provider>
  );
}
